'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import type { SectionOf } from '@/lib/home-v2/schema';

type Slide = SectionOf<'hero'>['slides'][number];

function Arrow({ dir, onClick }: { dir: 1 | -1; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} aria-label={dir === 1 ? 'Next slide' : 'Previous slide'}
      className={`absolute top-1/2 z-10 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-hv-ink shadow-md transition hover:bg-white md:flex md:opacity-0 md:group-hover:opacity-100 md:focus-visible:opacity-100 ${dir === 1 ? 'right-4' : 'left-4'}`}>
      <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d={dir === 1 ? 'M9 5l7 7-7 7' : 'M15 5l-7 7 7 7'} />
      </svg>
    </button>
  );
}

/**
 * Full-width image hero. Swipe on phones, arrows on hover, dots always.
 * Auto-play stops on hover, touch, focus, off-screen and reduced motion.
 */
export function HeroCarousel({ slides, autoPlayMs, height }: { slides: Slide[]; autoPlayMs: number; height: { desktop: number; mobile: number } }) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const [visible, setVisible] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const count = slides.length;

  const measure = useCallback(() => {
    const el = trackRef.current;
    if (el && el.clientWidth) setActive(Math.round(el.scrollLeft / el.clientWidth));
  }, []);

  const goTo = useCallback((i: number) => {
    const el = trackRef.current;
    if (el) el.scrollTo({ left: i * el.clientWidth, behavior: reducedMotion ? 'auto' : 'smooth' });
  }, [reducedMotion]);

  const move = useCallback((dir: 1 | -1) => goTo((active + dir + count) % count), [active, count, goTo]);

  useEffect(() => {
    setReducedMotion(window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    const el = trackRef.current;
    if (!el) return;
    el.addEventListener('scroll', measure, { passive: true });
    window.addEventListener('resize', measure);
    const io = new IntersectionObserver(([e]) => setVisible(e.isIntersecting), { threshold: 0.4 });
    io.observe(el);
    return () => {
      el.removeEventListener('scroll', measure);
      window.removeEventListener('resize', measure);
      io.disconnect();
    };
  }, [measure]);

  useEffect(() => {
    if (paused || !visible || reducedMotion || count < 2) return;
    const t = setInterval(() => { if (!document.hidden) move(1); }, autoPlayMs);
    return () => clearInterval(t);
  }, [paused, visible, reducedMotion, count, autoPlayMs, move]);

  if (count === 0) return null;

  return (
    <div className="group relative" aria-roledescription="carousel" aria-label="Featured"
      onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)} onBlurCapture={() => setPaused(false)}
      onTouchStart={() => setPaused(true)}
      onKeyDown={(e) => {
        if (e.key === 'ArrowRight') move(1);
        if (e.key === 'ArrowLeft') move(-1);
      }}>
      <div ref={trackRef} className="scrollbar-hide flex snap-x snap-mandatory overflow-x-auto">
        {slides.map((s, k) => (
          <div key={`${s.image}-${k}`} role="group" aria-roledescription="slide" aria-label={`${k + 1} of ${count}`}
            className="w-full flex-shrink-0 snap-start">
            <div className="relative h-[var(--hv-hero-m)] w-full overflow-hidden bg-hv-card md:h-[var(--hv-hero-d)]"
              style={{ '--hv-hero-m': `${height.mobile}px`, '--hv-hero-d': `${height.desktop}px` } as CSSProperties}>
              <Image src={s.image} alt={s.alt} fill sizes="100vw" priority={k === 0} loading={k === 0 ? 'eager' : 'lazy'}
                className={`object-cover ${s.mobileImage ? 'hidden md:block' : ''}`} />
              {s.mobileImage && (
                <Image src={s.mobileImage} alt={s.alt} fill sizes="100vw" priority={k === 0} loading={k === 0 ? 'eager' : 'lazy'} className="object-cover md:hidden" />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/35 to-transparent md:bg-gradient-to-r md:from-black/70 md:via-black/30 md:to-transparent" />
              <div className="absolute inset-0 flex items-end md:items-center">
                <div className="mx-auto flex w-full max-w-[1440px] flex-col gap-3 px-4 pb-10 text-white md:gap-5 md:px-10 md:pb-0 xl:px-16">
                  <div className="flex max-w-[640px] flex-col gap-3 md:gap-4">
                    {s.eyebrow && <div className="font-hv-mono text-[11px] uppercase tracking-[0.08em] text-hv-yellow md:text-[13px]">{s.eyebrow}</div>}
                    {/* Only the first slide is the page's h1 */}
                    {k === 0
                      ? <h1 className="text-[30px] leading-[1.05] tracking-[-0.02em] md:text-[64px]">{s.title}</h1>
                      : <h2 className="text-[30px] leading-[1.05] tracking-[-0.02em] md:text-[64px]">{s.title}</h2>}
                    {s.text && <p className="text-[15px] text-white/80 md:text-[18px]">{s.text}</p>}
                    {s.cta && (
                      <Link href={s.cta.href}
                        className="mt-1 inline-flex w-fit items-center gap-2 rounded-xl bg-hv-yellow px-5 py-3 font-semibold text-hv-ink transition hover:bg-white md:mt-2">
                        {s.cta.label}
                        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {count > 1 && (
        <>
          <Arrow dir={-1} onClick={() => move(-1)} />
          <Arrow dir={1} onClick={() => move(1)} />
          <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 items-center gap-1.5 md:bottom-6 md:left-auto md:right-10 md:translate-x-0 xl:right-16">
            {slides.map((_, k) => (
              <button key={k} type="button" aria-label={`Show slide ${k + 1}`} aria-current={k === active} onClick={() => goTo(k)}
                className={`h-1.5 rounded-full transition-all ${k === active ? 'w-7 bg-white' : 'w-1.5 bg-white/50'}`} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
